import { useState } from "react";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { CHANGE_LABELS, CHANGE_TONES, DIFF_GROUP_LABELS, DIFF_GROUP_ORDER, changeLabel, deltaLines, kindLabel, moduleLabel, shortSha, totalsOf } from "../../lib/architecture";
import { KindBadge, SourceRef } from "./ArchitectureBits";
import { Button } from "../ui/button";

const CHANGE_ORDER = ["added", "removed", "renamed", "modified"];

function ChangeBadge({ item }) {
  return (
    <span data-change={item.change} className={`inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 text-2xs font-medium ${CHANGE_TONES[item.change]}`}>
      {changeLabel(item)}
    </span>
  );
}

function Counts({ counts }) {
  return (
    <span className="flex flex-wrap gap-1.5 text-2xs">
      {CHANGE_ORDER.filter((c) => counts[c]).map((c) => (
        <span key={c} className={`rounded border px-1.5 py-0.5 font-medium ${CHANGE_TONES[c]}`}>{counts[c]} {CHANGE_LABELS[c].toLowerCase()}</span>
      ))}
    </span>
  );
}

function ItemRow({ item, onOpen }) {
  const lines = deltaLines(item.delta);
  return (
    <li className="px-3 py-2" data-testid="diff-item">
      <div className="flex flex-wrap items-center gap-2">
        <ChangeBadge item={item} />
        <KindBadge kind={item.kind} />
        {item.change === "removed" ? <span className="min-w-0 break-all text-sm text-slate-900">{item.name}</span>
          : <button type="button" onClick={() => onOpen(item.id)} className="min-w-0 break-all text-left text-sm font-medium text-jamora hover:underline">{item.name}</button>}
      </div>
      {item.previous ? <p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-600"><span className="break-all">{item.previous.path || item.previous.name}</span><ArrowRight size={12} aria-hidden /><span className="break-all">{item.path}</span><span className="text-slate-500">— {item.reason}</span></p> : null}
      {lines.length ? <ul className="mt-1 space-y-0.5 text-xs text-slate-700">{lines.map((l) => <li key={l} className="break-words">{l}</li>)}</ul> : null}
      {item.path && !item.previous ? <div className="mt-1"><SourceRef path={item.path} line={item.line} /></div> : null}
    </li>
  );
}

function Group({ id, data, onOpen }) {
  const [open, setOpen] = useState(id !== "files" && id !== "symbols");
  return (
    <section className="rounded-lg border border-slate-200 bg-white" data-testid={`diff-group-${id}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-left [@media(pointer:coarse)]:min-h-11">
        <span className="text-sm font-semibold text-slate-900">{DIFF_GROUP_LABELS[id] || id}</span>
        <Counts counts={data.counts} />
      </button>
      {open ? (
        <>
          <ul className="divide-y divide-slate-100 border-t border-slate-100">{data.items.map((item) => <ItemRow key={`${item.change}:${item.id}`} item={item} onOpen={onOpen} />)}</ul>
          {data.truncated ? <p className="border-t border-slate-100 px-3 py-2 text-xs text-slate-500">{data.items.length} sur {data.total} affichés.</p> : null}
        </>
      ) : null}
    </section>
  );
}

function RelationList({ title, block, testid }) {
  const [open, setOpen] = useState(false);
  if (!block.total) return null;
  return (
    <section className="rounded-lg border border-slate-200 bg-white" data-testid={testid}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-left [@media(pointer:coarse)]:min-h-11">
        <span className="text-sm font-semibold text-slate-900">{title}</span>
        <span className="text-xs text-slate-600">{block.total.toLocaleString("fr-FR")} · {Object.entries(block.by_kind).slice(0, 4).map(([k, n]) => `${k} ${n}`).join(", ")}</span>
      </button>
      {open ? (
        <ul className="divide-y divide-slate-100 border-t border-slate-100">
          {block.items.map((r, i) => (
            <li key={i} className="px-3 py-2 text-xs text-slate-700">
              <span className="font-medium text-slate-900">{r.kind}</span> : <span className="break-all">{r.from.name || r.from.id}</span> <ArrowRight size={11} className="inline" aria-hidden /> <span className="break-all">{r.to.name || r.to.id}</span>
              {r.path ? <div className="mt-0.5"><SourceRef path={r.path} line={r.line} /></div> : null}
            </li>
          ))}
          {block.total > block.items.length ? <li className="px-3 py-2 text-xs text-slate-500">{block.items.length} sur {block.total.toLocaleString("fr-FR")} affichées.</li> : null}
        </ul>
      ) : null}
    </section>
  );
}

function ImpactList({ title, block, onOpen }) {
  if (!block.total) return null;
  return (
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title} ({block.total})</h4>
      <ul className="mt-1 flex flex-wrap gap-1.5">
        {block.items.slice(0, 40).map((i) => (
          <li key={i.id}><button type="button" onClick={() => onOpen(i.id)} className="inline-flex max-w-full items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-1 text-xs text-slate-800 hover:border-jamora [@media(pointer:coarse)]:min-h-11"><span className="text-slate-500">{kindLabel(i.kind)}</span><span className="break-all">{i.name}</span></button></li>
        ))}
      </ul>
      {block.total > 40 ? <p className="mt-1 text-xs text-slate-500">40 sur {block.total} affichés.</p> : null}
    </div>
  );
}

// Comparison of two published versions (lot CRM-4). The impact is a potential one, deduced from the registry's relations: never presented as a regression.
export default function DiffView({ diff, onOpenNode, onClear }) {
  const totals = totalsOf(diff.summary);
  const groups = DIFF_GROUP_ORDER.filter((id) => diff.groups[id]);
  const modules = diff.modules;
  return (
    <div className="space-y-3" data-testid="arch-diff">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
          Comparaison <code className="rounded bg-slate-100 px-1 font-normal">{shortSha(diff.from.commit_sha)}</code><ArrowRight size={14} aria-hidden /><code className="rounded bg-slate-100 px-1 font-normal">{shortSha(diff.to.commit_sha)}</code>
        </h3>
        <Button variant="ghost" size="sm" onClick={onClear} data-testid="diff-close">Fermer la comparaison</Button>
      </div>

      {diff.same_content ? (
        <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700" data-testid="diff-empty">Ces deux versions ont exactement le même contenu : rien n'a changé dans la cartographie.</p>
      ) : (
        <>
          <p className="text-sm text-slate-700" data-testid="diff-totals">
            {totals.added} ajouté(s), {totals.removed} supprimé(s), {totals.renamed} renommé(s), {totals.modified} modifié(s) · relations : + {diff.relations.added.total.toLocaleString("fr-FR")} / − {diff.relations.removed.total.toLocaleString("fr-FR")} · à investiguer : + {diff.unresolved.added.total} / − {diff.unresolved.removed.total}
          </p>
          {groups.length ? groups.map((id) => <Group key={id} id={id} data={diff.groups[id]} onOpen={onOpenNode} />)
            : <p className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700">Aucun élément n'a changé de structure ; seules des relations ou des lignes ont bougé.</p>}

          <div className="space-y-2">
            <RelationList title="Relations ajoutées" block={diff.relations.added} testid="diff-relations-added" />
            <RelationList title="Relations supprimées" block={diff.relations.removed} testid="diff-relations-removed" />
          </div>

          {diff.unresolved.added.total || diff.unresolved.removed.total ? (
            <section className="rounded-lg border border-slate-200 bg-white p-3" data-testid="diff-unresolved">
              <h4 className="text-sm font-semibold text-slate-900">Changements non résolus</h4>
              <p className="mt-0.5 text-xs text-slate-600">Ce que l'analyse n'a pas pu trancher a changé : {diff.unresolved.added.total} nouveau(x), {diff.unresolved.removed.total} levé(s) ou disparu(s).</p>
              <ul className="mt-2 space-y-1 text-xs text-slate-700">
                {diff.unresolved.added.items.slice(0, 20).map((u, i) => <li key={i} className="break-words"><span className="font-medium">{u.kind}</span> — {u.detail || u.path}{u.path ? <span className="text-slate-500"> ({u.path}{u.line ? `:${u.line}` : ""})</span> : null}</li>)}
              </ul>
            </section>
          ) : null}

          {modules.added.length || modules.removed.length || modules.changed.length ? (
            <section className="rounded-lg border border-slate-200 bg-white p-3" data-testid="diff-modules">
              <h4 className="text-sm font-semibold text-slate-900">Domaines touchés</h4>
              <p className="mt-1 text-xs text-slate-700">
                {modules.added.length ? <>Nouveaux : {modules.added.map(moduleLabel).join(", ")}. </> : null}
                {modules.removed.length ? <>Disparus : {modules.removed.map(moduleLabel).join(", ")}. </> : null}
                {modules.changed.length ? <>Modifiés : {modules.changed.slice(0, 12).map((m) => `${moduleLabel(m)} (${modules.counts[m].before} → ${modules.counts[m].after})`).join(", ")}.</> : null}
              </p>
            </section>
          ) : null}

          <section className="space-y-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3" data-testid="diff-impact">
            <div className="flex items-start gap-2 text-sm text-amber-900"><AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden /><span>{diff.impact.warning}</span></div>
            <ImpactList title="Ce qui utilise les éléments modifiés (impact inverse)" block={diff.impact.upstream} onOpen={onOpenNode} />
            <ImpactList title="Ce que les éléments modifiés utilisent (impact descendant)" block={diff.impact.downstream} onOpen={onOpenNode} />
            {diff.impact.journeys.length ? (
              <div data-testid="diff-journeys">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Parcours potentiellement touchés ({diff.impact.journeys.length})</h4>
                <ul className="mt-1 flex flex-wrap gap-1.5">{diff.impact.journeys.slice(0, 30).map((j) => <li key={j.id}><button type="button" onClick={() => onOpenNode(j.id)} className="rounded border border-slate-200 bg-white px-1.5 py-1 text-xs text-slate-800 hover:border-jamora [@media(pointer:coarse)]:min-h-11">{j.name}</button></li>)}</ul>
              </div>
            ) : null}
          </section>
        </>
      )}
    </div>
  );
}
